import type { Column } from '@components/Common/SearchFilter/SearchFilter';

/**
 * Normalize any legacy or partial phone value to an E.164-ish string the phone
 * input can display (e.g. "9876543210" or "+91 98765 43210" -> "+919876543210").
 * India is assumed when no country code is present, matching the historical default.
 */
export const normalizeToE164 = (raw?: string | null): string => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) return trimmed.replace(/\s+/g, '');
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+91${digits}`;
  if (digits.startsWith('91') && digits.length === 12) return `+${digits}`;
  if (digits.startsWith('0') && digits.length === 11) return `+91${digits.slice(1)}`;
  return `+${digits}`;
};

export const countryCode = {
  AFG: 'Afghanistan',
  ALB: 'Albania',
  DZA: 'Algeria',
  AND: 'Andorra',
  AGO: 'Angola',
  ARG: 'Argentina',
  ARM: 'Armenia',
  AUS: 'Australia',
  AUT: 'Austria',
  AZE: 'Azerbaijan',
  BHS: 'Bahamas',
  BHR: 'Bahrain',
  BGD: 'Bangladesh',
  BRB: 'Barbados',
  BLR: 'Belarus',
  BEL: 'Belgium',
  BLZ: 'Belize',
  BEN: 'Benin',
  BTN: 'Bhutan',
  BOL: 'Bolivia',
  BIH: 'Bosnia and Herzegovina',
  BWA: 'Botswana',
  BRA: 'Brazil',
  BRN: 'Brunei',
  BGR: 'Bulgaria',
  BFA: 'Burkina Faso',
  BDI: 'Burundi',
  CPV: 'Cabo Verde',
  KHM: 'Cambodia',
  CMR: 'Cameroon',
  CAN: 'Canada',
  CAF: 'Central African Republic',
  TCD: 'Chad',
  CHL: 'Chile',
  CHN: 'China',
  COL: 'Colombia',
  COM: 'Comoros',
  COD: 'Congo (Congo-Kinshasa)',
  COG: 'Congo (Congo-Brazzaville)',
  CRI: 'Costa Rica',
  CIV: 'Côte d’Ivoire',
  HRV: 'Croatia',
  CUB: 'Cuba',
  CYP: 'Cyprus',
  CZE: 'Czechia',
  DNK: 'Denmark',
  DJI: 'Djibouti',
  DMA: 'Dominica',
  DOM: 'Dominican Republic',
  ECU: 'Ecuador',
  EGY: 'Egypt',
  SLV: 'El Salvador',
  GNQ: 'Equatorial Guinea',
  ERI: 'Eritrea',
  EST: 'Estonia',
  SWZ: 'Eswatini',
  ETH: 'Ethiopia',
  FJI: 'Fiji',
  FIN: 'Finland',
  FRA: 'France',
  GAB: 'Gabon',
  GMB: 'Gambia',
  GEO: 'Georgia',
  DEU: 'Germany',
  GHA: 'Ghana',
  GRC: 'Greece',
  GRD: 'Grenada',
  GTM: 'Guatemala',
  GIN: 'Guinea',
  GNB: 'Guinea-Bissau',
  GUY: 'Guyana',
  HTI: 'Haiti',
  HND: 'Honduras',
  HUN: 'Hungary',
  ISL: 'Iceland',
  IND: 'India',
  IDN: 'Indonesia',
  IRN: 'Iran',
  IRQ: 'Iraq',
  IRL: 'Ireland',
  ISR: 'Israel',
  ITA: 'Italy',
  JAM: 'Jamaica',
  JPN: 'Japan',
  JOR: 'Jordan',
  KAZ: 'Kazakhstan',
  KEN: 'Kenya',
  KIR: 'Kiribati',
  KOR: 'South Korea',
  KWT: 'Kuwait',
  KGZ: 'Kyrgyzstan',
  LAO: 'Laos',
  LVA: 'Latvia',
  LBN: 'Lebanon',
  LSO: 'Lesotho',
  LBR: 'Liberia',
  LBY: 'Libya',
  LIE: 'Liechtenstein',
  LTU: 'Lithuania',
  LUX: 'Luxembourg',
  MDG: 'Madagascar',
  MWI: 'Malawi',
  MYS: 'Malaysia',
  MDV: 'Maldives',
  MLI: 'Mali',
  MLT: 'Malta',
  MHL: 'Marshall Islands',
  MRT: 'Mauritania',
  MUS: 'Mauritius',
  MEX: 'Mexico',
  FSM: 'Micronesia',
  MDA: 'Moldova',
  MCO: 'Monaco',
  MNG: 'Mongolia',
  MNE: 'Montenegro',
  MAR: 'Morocco',
  MOZ: 'Mozambique',
  MMR: 'Myanmar (Burma)',
  NAM: 'Namibia',
  NRU: 'Nauru',
  NPL: 'Nepal',
  NLD: 'Netherlands',
  NZL: 'New Zealand',
  NIC: 'Nicaragua',
  NER: 'Niger',
  NGA: 'Nigeria',
  NOR: 'Norway',
  OMN: 'Oman',
  PAK: 'Pakistan',
  PLW: 'Palau',
  PAN: 'Panama',
  PNG: 'Papua New Guinea',
  PRY: 'Paraguay',
  PER: 'Peru',
  PHL: 'Philippines',
  POL: 'Poland',
  PRT: 'Portugal',
  QAT: 'Qatar',
  ROU: 'Romania',
  RUS: 'Russia',
  RWA: 'Rwanda',
  WSM: 'Samoa',
  SMR: 'San Marino',
  SAU: 'Saudi Arabia',
  SEN: 'Senegal',
  SRB: 'Serbia',
  SYC: 'Seychelles',
  SLE: 'Sierra Leone',
  SGP: 'Singapore',
  SVK: 'Slovakia',
  SVN: 'Slovenia',
  SLB: 'Solomon Islands',
  SOM: 'Somalia',
  ZAF: 'South Africa',
  SSD: 'South Sudan',
  ESP: 'Spain',
  LKA: 'Sri Lanka',
  SDN: 'Sudan',
  SUR: 'Suriname',
  SWE: 'Sweden',
  CHE: 'Switzerland',
  SYR: 'Syria',
  TWN: 'Taiwan',
  TJK: 'Tajikistan',
  TZA: 'Tanzania',
  THA: 'Thailand',
  TLS: 'Timor-Leste',
  TGO: 'Togo',
  TON: 'Tonga',
  TTO: 'Trinidad and Tobago',
  TUN: 'Tunisia',
  TUR: 'Turkey',
  TKM: 'Turkmenistan',
  TUV: 'Tuvalu',
  UGA: 'Uganda',
  UKR: 'Ukraine',
  ARE: 'United Arab Emirates',
  GBR: 'United Kingdom',
  USA: 'United States',
  URY: 'Uruguay',
  UZB: 'Uzbekistan',
  VUT: 'Vanuatu',
  VEN: 'Venezuela',
  VNM: 'Vietnam',
  YEM: 'Yemen',
  ZMB: 'Zambia',
  ZWE: 'Zimbabwe',
};

export const graphQLFilterFormatter = (
  filterData: Column[],
  columnMapping: any,
  booleanColumns: string[] = [],
) => {
  const result = filterData.map((data) => {
    if (booleanColumns.includes(data.columnName)) {
      return `{ key: "${columnMapping[data.columnName]}", boolValue: ${data.searchValue === 'true' ? 'true' : 'false'
        } }`;
    }
    return `{ key: "${columnMapping[data.columnName]}", value: "${data.searchValue}" }`;
  });
  return result;
};

/**
 * A `[{key, value}]` list, ready to inline into a mutation string.
 *
 * Both sides go through JSON.stringify, which produces exactly GraphQL's string syntax for
 * quotes, backslashes and newlines. It used to interpolate them raw, between hand-written quote
 * characters, and these lists carry free text: a job's `internalFilters` and a record's SEO
 * `metadata` are typed by staff. One `"` in a value produced a document that would not parse, so
 * the whole mutation was refused and the save reported a generic failure.
 *
 * Callers must NOT pre-escape. `sanitizeKeyValues` used to hand-roll `.replace(/"/g, '\\"')`
 * before calling this; escaping twice is as broken as not escaping at all, just less obviously.
 */
export const graphQLArrayFormatter = (data: any[]) => {
  const result = (data ?? [])
    .map(({ key, value }) => `{ key: ${JSON.stringify(key ?? '')}, value: ${JSON.stringify(value ?? '')} }`)
    .join(', ');
  return `[${result}]`;
};

export const graphQLFilterArrayFormatter = (data: any[]): string[] => {
  return data.map(({ key, value, operator }) =>
    `{ key: "${key}", value: "${value}", operator: "${operator}" }`
  );
};

/**
 * Builds a filter array for GraphQL queries from UI filter state.
 * - Array field mappings → OR across all mapped fields
 * - locationPrefMapping fields → OR between the primary field and the preference field
 * - All other fields → AND
 */
export const buildFilterArray = (
  filters: Column[],
  colMapping: Record<string, string | string[]>,
  locationPrefMapping?: Record<string, string>,
): { key: string; value: string; operator: string }[] => {
  const result: { key: string; value: string; operator: string }[] = [];
  filters.forEach((filter) => {
    const mapping = colMapping[filter.columnName];
    if (Array.isArray(mapping)) {
      mapping.forEach((field) => {
        result.push({ key: field, value: filter.searchValue, operator: 'OR' });
      });
    } else if (locationPrefMapping?.[filter.columnName]) {
      result.push({ key: mapping as string, value: filter.searchValue, operator: 'OR' });
      result.push({ key: locationPrefMapping[filter.columnName], value: filter.searchValue, operator: 'OR' });
    } else {
      result.push({ key: mapping as string, value: filter.searchValue, operator: 'AND' });
    }
  });
  return result;
};

/**
 * Sanitizes HTML from a rich text editor before rendering via dangerouslySetInnerHTML.
 * Strips script/style tags, on* event attributes, and javascript: hrefs.
 * Allows a safe subset of formatting tags only.
 */
export const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('script,style').forEach((el) => el.remove());

    const allowedTags = new Set([
      'B', 'I', 'U', 'STRONG', 'EM', 'BR', 'DIV', 'SPAN', 'P',
      'UL', 'OL', 'LI', 'A',
    ]);

    const elements = Array.from(doc.body.querySelectorAll('*')) as Element[];
    elements.forEach((el) => {
      const tag = el.tagName.toUpperCase();
      if (!allowedTags.has(tag)) {
        const parent = el.parentNode;
        while (el.firstChild) parent?.insertBefore(el.firstChild, el);
        parent?.removeChild(el);
        return;
      }
      Array.from(el.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        const val = attr.value || '';
        if (name.startsWith('on')) { el.removeAttribute(attr.name); return; }
        /*
         * Links keep only schemes that cannot run code (security audit 2026-10-31, H4).
         *
         * This refused only values starting with "javascript:" after trim, and browsers drop tabs and
         * newlines inside a scheme, so `java\nscript:` ran on click; `data:` and `vbscript:` were
         * never looked at. Control characters and whitespace are removed before the scheme is read,
         * and anything other than http(s), mailto, tel or a relative link is dropped.
         */
        if (name === 'href' || name === 'src') {
          // eslint-disable-next-line no-control-regex
          const compact = val.replace(/[\u0000- \u007f-\u009f]/g, '').toLowerCase();
          const scheme = /^([a-z][a-z0-9+.-]*):/.exec(compact)?.[1];
          if (scheme && !['http', 'https', 'mailto', 'tel'].includes(scheme)) {
            el.removeAttribute(attr.name);
            return;
          }
        }
        if (name !== 'href' && name !== 'title' && name !== 'alt') {
          el.removeAttribute(attr.name);
        }
      });
    });

    return doc.body.innerHTML;
  } catch {
    return '';
  }
};

/**
 * Renders stored note HTML as plain text.
 *
 * Notes were composed in a rich-text editor and stored as HTML. That editor is gone — a note is a
 * line of prose, not a document — but the rows already written are still HTML, and two surfaces
 * disagreed about them: the notes list ran them through `dangerouslySetInnerHTML` while the
 * activity timeline printed the same string as text, so an empty line typed in the old editor
 * surfaced to the user as a literal `<div><br></div>`.
 *
 * Block boundaries become newlines *before* the tags are dropped. Stripping naively would run
 * every paragraph and list item of every existing note into one unreadable line — the tags are the
 * only record of that structure, so they have to be spent, not discarded. Render the result with
 * `white-space: pre-wrap`.
 *
 * Built on DOMParser rather than a tag regex so entities (`&amp;`, `&nbsp;`) decode, and so text
 * that merely looks like a tag cannot be eaten.
 */
export const htmlToText = (html: string): string => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('script,style').forEach((el) => el.remove());
    doc.querySelectorAll('br').forEach((el) => el.replaceWith('\n'));
    // A trailing newline per block, then collapse: <div><br></div> yields one break, not three.
    doc.querySelectorAll('p,div,li,tr').forEach((el) => el.append('\n'));

    return (doc.body.textContent ?? '')
      .replace(/ /g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  } catch {
    // Unparseable input is not worth losing the note over: show it as-is rather than blank.
    return html;
  }
};
